"use client"

import type React from "react"
import { useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useSyncOptions } from "../../../hooks/useSyncOptions"
import { useSyncSelection } from "../../../hooks/useSyncSelection"
import { CHOOSE_SYNC_CONSTANTS } from "../../../constants/choose-sync"

// Components
import LoadingScreen from "../shared/LoadingScreen"
import ErrorScreen from "../shared/ErrorScreen"
import StepIndicator from "../shared/StepIndicator"
import SyncOptionsList from "./SyncOptionsList"

import "./ChooseSync.css"

const ChooseSyncConnectScreen: React.FC = () => {
  const navigate = useNavigate()
  const { syncOptions, loading, error, fetchSyncOptions, retryFetch } = useSyncOptions()
  const { selectedOption, selectOption, proceedToNext } = useSyncSelection()

  // Load sync options on mount
  useEffect(() => {
    fetchSyncOptions()
  }, [fetchSyncOptions])

  const handleBack = useCallback(() => {
    navigate(CHOOSE_SYNC_CONSTANTS.ROUTES.CONNECT_ACCOUNTS)
  }, [navigate])

  const handleContinue = useCallback(() => {
    if (proceedToNext()) {
      navigate(`${CHOOSE_SYNC_CONSTANTS.ROUTES.MAP_FIELDS}?id=${selectedOption}`)
    }
  }, [proceedToNext, selectedOption, navigate])

  // Show loading screen
  if (loading) {
    return (
      <LoadingScreen
        title="Loading Sync Options"
        description="Please wait while we fetch available sync configurations..."
      />
    )
  }

  // Show error screen
  if (error && syncOptions.length === 0) {
    return <ErrorScreen title="Unable to Load Sync Options" message={error} onRetry={retryFetch} />
  }

  return (
    <div className="choose-sync">
      <div className="choose-sync-header">
        <h1>Choose What to Sync</h1>
        <p>Select the data you'd like to sync between HubSpot and Salesforce.</p>
        <p>You can always update this later.</p>
        <StepIndicator
          currentStep={CHOOSE_SYNC_CONSTANTS.STEP_INFO.CURRENT_STEP}
          stepLabel={CHOOSE_SYNC_CONSTANTS.STEP_INFO.STEP_LABEL}
          completedSteps={CHOOSE_SYNC_CONSTANTS.STEP_INFO.COMPLETED_STEPS}
        />
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span>Failed to load some sync options. Showing default options.</span>
          <button onClick={retryFetch} className="retry-link">
            Retry
          </button>
        </div>
      )}

      <SyncOptionsList options={syncOptions} selectedOption={selectedOption} onSelectOption={selectOption} />

      <div className="settings-note">
        <p>💡 You can update this later in Settings</p>
      </div>

      <div className="actions-section">
        <button className="back-button" onClick={handleBack}>
          Back
        </button>
        <button className="continue-button" onClick={handleContinue} disabled={!selectedOption}>
          Continue
        </button>
      </div>
    </div>
  )
}

export default ChooseSyncConnectScreen
